import React, { useEffect, useRef, useState } from "react";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";

import * as S from "../styles";

const AnimatedHeight = ({ children }) => {
  const innerRef = useRef(null);
  const prevHeightRef = useRef(null);
  const [height, setHeight] = useState(null);
  const [animating, setAnimating] = useState(false);

  useEffect(() => {
    const el = innerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => {
      const next = el.offsetHeight;
      if (prevHeightRef.current !== null && prevHeightRef.current !== next) {
        setAnimating(true);
      }
      prevHeightRef.current = next;
      setHeight(next);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      style={{
        height: height === null ? "auto" : height,
        transition: "height 280ms ease",
        overflow: animating ? "hidden" : "visible",
      }}
      onTransitionEnd={e => {
        if (e.propertyName === "height") setAnimating(false);
      }}
    >
      <div ref={innerRef}>{children}</div>
    </div>
  );
};

const CollapsibleSection = ({
  title,
  step,
  active,
  completed,
  summary,
  children,
}) => {
  const cardRef = useRef(null);
  const [open, setOpen] = useState(true);

  // Collapse only when section is complete AND focus is not inside it.
  // Never collapse while the user is editing (covers optional fields like
  // "Complemento" and value flickers while typing).
  useEffect(() => {
    if (!completed) {
      setOpen(true);
    } else if (!cardRef.current?.contains(document.activeElement)) {
      setOpen(false);
    }
  }, [completed]);

  const collapsed = completed && !open;

  const expand = () => {
    setOpen(true);
    setTimeout(() => {
      cardRef.current?.querySelector("input:not([disabled])")?.focus();
    }, 60);
  };

  const handleBlur = e => {
    if (completed && !e.currentTarget.contains(e.relatedTarget)) {
      setOpen(false);
    }
  };

  return (
    <S.SectionCard
      ref={cardRef}
      tabIndex={-1}
      style={{ outline: "none" }}
      onBlur={handleBlur}
    >
      <AnimatedHeight>
        {collapsed ? (
          <S.FadeIn>
            <S.CollapsedHeader>
              <div className="info">
                <div className="title-row">
                  <CheckCircleOutlineOutlinedIcon
                    style={{ fontSize: 26, color: "#01AB7D" }}
                  />
                  <h2>{title}</h2>
                </div>
                <p className="summary">{summary}</p>
              </div>
              <S.ChangeButton type="button" onClick={expand}>
                Mudar
              </S.ChangeButton>
            </S.CollapsedHeader>
          </S.FadeIn>
        ) : (
          <S.FadeIn>
            <S.SectionHeader>
              {completed ? (
                <CheckCircleOutlineOutlinedIcon
                  style={{ fontSize: 26, color: "#01AB7D" }}
                />
              ) : (
                <S.StepBadge active={active}>{step}</S.StepBadge>
              )}
              <h2>{title}</h2>
            </S.SectionHeader>
            {children}
          </S.FadeIn>
        )}
      </AnimatedHeight>
    </S.SectionCard>
  );
};

export default CollapsibleSection;
